import * as React from "react";
import {Dispatch, FC, SetStateAction} from "react";
import {Divider, Link, ThemeProvider} from "@mui/material";
import TranslateSafeLoadingButton from "../../shared/translate-safe-loading-button/TranslateSafeLoadingButton";
import {whiteInputsTheme} from "../../../util/theme/whiteInputsTheme";
import CustomDialog from "../../shared/custom-dialog/CustomDialog";
import Box from "@mui/material/Box";
import {RootState, useAppDispatch, useAppSelector} from "../../../store";
import Typography from "@mui/material/Typography";
import {Trans, useTranslation} from "react-i18next";
import {IWish} from "../../../store/wish/Helper";
import {obtainFreeVoucher} from "../../../store/ticket/ActionCreators";
import ReactGA from "react-ga4";
import {useTheme} from "@mui/material/styles";
import {toastSuccess} from "../../shared/toast/Toast";
import {formatDate} from "../../../util/utils";
import {Link as RouterLink} from "react-router-dom";
import {RoutePaths} from "../../../util/constants";

interface IProps {
    open: boolean
    handleDialogClose: () => void
    wish: IWish
    hasParticipated: boolean
    setHasParticipated: Dispatch<SetStateAction<boolean>>
}

const ObtainFreeVoucherDialog: FC<IProps> = (props) => {
    const {i18n, t} = useTranslation()
    const dispatch = useAppDispatch()
    const obtainFreeVoucherLoading = useAppSelector((state: RootState) => state.ticketReducer.loading)
    const theme = useTheme()


    const updateHasParticipated = () => {
        if (props.wish.cost === 0) {
            props.setHasParticipated(true)
        }
        props.handleDialogClose()
        toastSuccess(t('success_obtain_free_voucher'))
    }

    const handleObtainFreeVoucher = () => {
        if (props.hasParticipated) {
            props.handleDialogClose()
            return
        }
        ReactGA.event({
            category: 'Wish',
            action: 'make_a_wish_click_confirm',
            label: `Obtain voucher - Confirm - ${props.wish.id}`,
        })
        dispatch(obtainFreeVoucher({
            body: {
                wish_id: props.wish.id,
            },
            updateHasParticipated
        }))
    }


    const dialogActions = (
        <TranslateSafeLoadingButton
            variant="contained"
            loading={obtainFreeVoucherLoading}
            onClick={handleObtainFreeVoucher}
        >
            {!props.hasParticipated ? t('confirm') : t('close')}
        </TranslateSafeLoadingButton>
    )

    const dialogContent = (
        <Box my={2}>
            <Typography variant='subtitle1' color='primary'>
                {t('obtain_free_voucher_confirmation', {
                    wish_name: props.wish[`name_${i18n.language}`]
                })}
            </Typography>

            <Typography variant='subtitle1' color='primary' fontWeight="600" sx={{textDecoration: "underline"}}
                        mt={2}>
                {t('free_voucher_note')}
            </Typography>

            {!props.hasParticipated && <>
                <Divider sx={{backgroundColor: theme.palette.primary.main, mt: 2}}/>
                <Typography variant="body2" color="primary" mt={1}>
                    <Trans
                        i18nKey="obtain_voucher_notice"
                        values={{expiry: formatDate(props.wish.voucher_expiry, false)}}
                        components={[
                            <Link component={RouterLink} sx={{cursor: "pointer"}} color="primary"
                                  to={RoutePaths.TERMS} target="_blank" rel="noopener noreferrer"/>
                        ]}
                    />
                </Typography>
            </>}
        </Box>

    )

    return (
        <ThemeProvider theme={whiteInputsTheme}>
            <CustomDialog open={props.open}
                          title={t('obtain_voucher')}
                          maxWidth='sm'
                          actions={dialogActions}
                          handleDialogClose={props.handleDialogClose}
                          content={dialogContent}/>
        </ThemeProvider>
    )
}

export default ObtainFreeVoucherDialog
